import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Users, Clock, CheckCircle2 } from "lucide-react";
import { API } from "@/App";

const todayISO = () => new Date().toISOString().split("T")[0];

const formatDateFR = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const inputCls =
  "w-full rounded-xl border border-stone-200 bg-cream/60 px-4 py-3 text-sm text-stone-900 outline-none transition-all placeholder:text-stone-400 focus:border-burgundy focus:ring-2 focus:ring-burgundy/15";

export default function ReservationSection() {
  const [form, setForm] = useState({ name: "", email: "", guests: 2, date: "", service: "Déjeuner (12h00 - 13h15)", notes: "" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const isOpenDay = (iso) => {
    const day = new Date(`${iso}T12:00:00`).getDay();
    return day === 4 || day === 5;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isOpenDay(form.date)) {
      toast.error("Date indisponible", {
        description: "Le restaurant est ouvert uniquement le jeudi et le vendredi (12h00 - 13h15).",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reservations`, {
        name: form.name,
        email: form.email,
        guests: Number(form.guests),
        date: form.date,
        service: form.service,
        notes: form.notes,
      });
      setConfirmation(data);
      toast.success("Réservation enregistrée", {
        description: "Elle sera confirmée prochainement par notre équipe.",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Réservation impossible", {
        description: typeof detail === "string" ? detail : "Veuillez réessayer dans un instant.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reservation" className="relative overflow-hidden bg-burgundy-deep py-20 lg:py-32" data-testid="reservation-section">
      <span aria-hidden className="pointer-events-none absolute -bottom-10 right-0 select-none font-serif text-[16vw] font-black italic leading-none text-white/[0.04]">
        Réserver
      </span>

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col justify-center"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Réservation</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-cream sm:text-5xl">
            Votre table vous attend,
            <span className="block italic text-gold-light">nos élèves aussi</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-cream/70">
            Réservez à votre nom, indiquez le nombre de convives et la date souhaitée.
            Notre équipe confirme ensuite votre venue — vous pouvez suivre son statut
            à tout moment dans la rubrique « Mon Statut ».
          </p>
          <div className="mt-10 space-y-4">
            {[
              { icon: CalendarCheck, text: "Ouvert le jeudi et le vendredi · 12h00 – 13h15" },
              { icon: Users, text: "Groupes jusqu'à 20 personnes" },
              { icon: Clock, text: "Menu unique : 5 € profs & élèves · 7 € externes" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm text-cream/80">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/30 bg-white/5">
                  <Icon className="h-4 w-4 text-gold" />
                </span>
                {text}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          {confirmation ? (
            <div className="rounded-3xl bg-white p-8 shadow-2xl sm:p-10" data-testid="reservation-success">
              <CheckCircle2 className="h-12 w-12 text-sage" />
              <h3 className="mt-4 font-serif text-2xl font-semibold text-stone-900">
                Merci, {confirmation.name} !
              </h3>
              <p className="mt-2 text-sm text-stone-600">Votre demande de réservation a bien été enregistrée.</p>
              <dl className="mt-6 space-y-3 rounded-2xl bg-cream p-5 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Email</dt>
                  <dd className="truncate font-semibold text-stone-900">{confirmation.email}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Enregistrée le</dt>
                  <dd className="font-semibold text-stone-900" data-testid="reservation-created-date">
                    {new Date(confirmation.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Date souhaitée</dt>
                  <dd className="font-semibold capitalize text-stone-900">{formatDateFR(confirmation.date)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Service</dt>
                  <dd className="font-semibold text-stone-900">{confirmation.service}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Convives</dt>
                  <dd className="font-semibold text-stone-900">{confirmation.guests} personne{confirmation.guests > 1 ? "s" : ""}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Statut</dt>
                  <dd className="rounded-full bg-gold-light px-3 py-0.5 text-xs font-semibold text-amber-900">En attente de confirmation</dd>
                </div>
              </dl>
              <button
                onClick={() => { setConfirmation(null); setForm({ name: "", email: "", guests: 2, date: "", service: "Déjeuner (12h00 - 13h15)", notes: "" }); }}
                className="mt-6 w-full rounded-full border border-burgundy/30 py-3 text-sm font-semibold text-burgundy transition-colors hover:bg-burgundy hover:text-cream"
                data-testid="reservation-new-button"
              >
                Faire une nouvelle réservation
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl bg-white p-8 shadow-2xl sm:p-10" data-testid="reservation-form">
              <h3 className="font-serif text-2xl font-semibold text-stone-900">Réserver une table</h3>
              <p className="mt-1 text-sm text-stone-500">Quelques informations suffisent.</p>

              <div className="mt-6 space-y-4">
                <div>
                  <label htmlFor="res-name" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Nom &amp; Prénom *</label>
                  <input id="res-name" required value={form.name} onChange={set("name")} placeholder="Jean Dupont" className={inputCls} data-testid="reservation-name-input" />
                </div>
                <div>
                  <label htmlFor="res-email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Adresse email *</label>
                  <input id="res-email" type="email" required value={form.email} onChange={set("email")} placeholder="jean.dupont@email.fr" className={inputCls} data-testid="reservation-email-input" />
                  <p className="mt-1 text-xs text-stone-400">Utilisée uniquement pour vous envoyer la confirmation de votre réservation.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="res-guests" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Personnes *</label>
                    <input id="res-guests" type="number" min="1" max="20" required value={form.guests} onChange={set("guests")} className={inputCls} data-testid="reservation-guests-input" />
                  </div>
                  <div>
                    <label htmlFor="res-date" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Date *</label>
                    <input id="res-date" type="date" required min={todayISO()} value={form.date} onChange={set("date")} className={inputCls} data-testid="reservation-date-input" />
                    <p className="mt-1 text-xs text-stone-400">Jeudi et vendredi uniquement.</p>
                  </div>
                </div>
                <div>
                  <label htmlFor="res-service" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Service *</label>
                  <select id="res-service" value={form.service} onChange={set("service")} className={inputCls} data-testid="reservation-service-select">
                    <option>Déjeuner (12h00 - 13h15)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="res-notes" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Info complémentaire (non obligatoire)</label>
                  <textarea id="res-notes" rows="3" value={form.notes} onChange={set("notes")} placeholder="Allergies, anniversaire, demande particulière…" className={`${inputCls} resize-none`} data-testid="reservation-notes-input" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-6 w-full rounded-full bg-burgundy py-4 text-sm font-semibold text-cream shadow-lg shadow-burgundy/25 transition-all hover:-translate-y-0.5 hover:bg-burgundy-dark disabled:opacity-60"
                data-testid="reservation-submit-button"
              >
                {loading ? "Envoi en cours…" : "Confirmer ma demande"}
              </button>
              <p className="mt-3 text-center text-xs text-stone-400">
                Enregistrée aujourd'hui, le {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
